import { initializeApp } from "firebase/app";
import { getAuth, signInWithPopup, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore/lite";
import { GoogleAuthProvider } from "firebase/auth";
import { partition } from "lodash";
import {
  business_days,
  CashflowItems,
  every_x_days,
  every_x_months,
  every_x_months_last_day,
  every_x_months_last_working_day,
  every_x_weeks,
  every_x_years,
  weekdays,
  RecurrenceRule,
  specific_dates,
  ParentalLeavePolicy,
  CashflowItem,
  dbCashflowItemSchema,
} from "./backend/types-v2";
import { ChronoUnit, DayOfWeek, LocalDate } from "@js-joda/core";

const provider = new GoogleAuthProvider();

const firebaseConfig = {
  prod: {
    apiKey: "AIzaSyBjVqNCkgaX9GGDv-CIbXfsnZPnpFfTLMo",
    authDomain: "moneyflux-prod.firebaseapp.com",
    projectId: "moneyflux-prod",
    storageBucket: "moneyflux-prod.appspot.com",
    messagingSenderId: "855646432227",
    appId: "1:855646432227:web:954a4c4443dc398ba3ced6",
  },
};

const firebase = initializeApp(firebaseConfig.prod);
const firestore = getFirestore(firebase);
export const auth = getAuth();

export const signOutUser = () => signOut(auth);

export const signInUser = async () => {
  await signInWithPopup(auth, provider);
};

export const getUser = async () => {
  const response = await signInWithPopup(auth, provider);
  return response;
};

export const loadData = async (): Promise<CashflowItems> => {
  if (!auth.currentUser) {
    console.log("Not signed in"); // return failure?
    return [];
  }

  const docRef = doc(firestore, "users", auth.currentUser?.uid);
  const rawResult = await getDoc(docRef);
  const result = rawResult.data();

  if (!result) {
    throw new Error("oopps");
  }

  const rawData = result.cashFlowItems;

  const safeParseItem = (income: any) => {
    const result = dbCashflowItemSchema.safeParse(income);

    if (result.success) {
      return {
        ...result,
      };
    }

    console.log(
      "failed to parse",
      JSON.stringify(income),
      JSON.stringify(result.error.errors),
    );

    return {
      result,
      unparseableData: income,
    };
  };

  const [parsedDbItems, unparsedDbItems] = partition(
    rawData.map(safeParseItem),
    (income) => income.success,
  );

  if (unparsedDbItems.length > 0) {
    console.error("🚀 ~ loadData ~ unparsedItems:", unparsedDbItems);
  }

  return parsedDbItems.map((item) => item.data).map(cashflowItemFromDb);
};

export const saveUserFormData = async (data: CashflowItems) => {
  if (!auth.currentUser) {
    return { success: false, message: "User not authenticated" };
  }

  try {
    const docRef = doc(firestore, "users", auth.currentUser?.uid);

    const dbItem = data.map(cashflowItemToDb);

    await setDoc(docRef, { cashFlowItems: dbItem }, { merge: true });
    return { success: true };
  } catch (e) {
    console.error(e);
    return { success: false, message: e };
  }
};

const ldToDb = (d: LocalDate) => d.toString();
const ldFromDb = (s: string) => LocalDate.parse(s);

const dowToDb = (d: DayOfWeek) => d.name();
const dowFromDb = (s: string) => DayOfWeek.valueOf(s);

const chronoFromDb = (s: string): ChronoUnit => {
  switch (s) {
    case ChronoUnit.DAYS.toString():
      return ChronoUnit.DAYS;
    case ChronoUnit.WEEKS.toString():
      return ChronoUnit.WEEKS;
    case ChronoUnit.MONTHS.toString():
      return ChronoUnit.MONTHS;
    case ChronoUnit.YEARS.toString():
      return ChronoUnit.YEARS;
    default:
      throw new Error(`Unknown ChronoUnit: ${s}`);
  }
};

const chronoToDb = (c: ChronoUnit) => c.toString();

export const recurrenceToDb = (r: RecurrenceRule): any => {
  switch (r.kind) {
    case every_x_days:
      return {
        ...r,
        startsOnWeekday: dowToDb(r.startsOnWeekday),
        recurrenceAnchorDate: ldToDb(r.recurrenceAnchorDate),
      };

    case every_x_weeks:
      return {
        ...r,
        weekday: dowToDb(r.weekday),
      };

    case every_x_months:
    case every_x_months_last_day:
    case every_x_months_last_working_day:
    case every_x_years:
    case business_days:
      return {
        ...r,
        recurrenceAnchorDate: ldToDb(r.recurrenceAnchorDate),
      };

    case weekdays:
      return {
        ...r,
        weekdays: r.weekdays.map(dowToDb),
        recurrenceAnchorDate: ldToDb(r.recurrenceAnchorDate),
      };

    case specific_dates:
      return {
        ...r,
        dates: r.dates.map(ldToDb),
      };
  }
};

export const recurrenceFromDb = (r: any): RecurrenceRule => {
  switch (r.kind) {
    case every_x_days:
      return {
        ...r,
        startsOnWeekday: dowFromDb(r.startsOnWeekday),
        recurrenceAnchorDate: ldFromDb(r.recurrenceAnchorDate),
      };

    case every_x_weeks:
      if (r.weekday) {
        return {
          kind: r.kind,
          interval: r.interval,
          weekday: dowFromDb(r.weekday),
        };
      }
      if (r.recurrenceAnchorDate) {
        const start = ldFromDb(r.recurrenceAnchorDate);
        return {
          kind: r.kind,
          interval: r.interval,
          weekday: start.dayOfWeek(),
        };
      }
      throw new Error("every-x-weeks missing weekday");

    case every_x_months:
    case every_x_months_last_day:
    case every_x_months_last_working_day:
    case every_x_years:
    case business_days:
      return {
        ...r,
        recurrenceAnchorDate: ldFromDb(r.recurrenceAnchorDate),
      };

    case weekdays:
      return {
        ...r,
        weekdays: r.weekdays.map(dowFromDb),
        recurrenceAnchorDate: ldFromDb(r.recurrenceAnchorDate),
      };

    case specific_dates:
      return {
        ...r,
        dates: r.dates.map(ldFromDb),
      };
  }

  throw new Error("Unsupported!");
};

const dateFiltersToDb = (df?: {
  dateFromInclusive?: LocalDate;
  dateToInclusive?: LocalDate;
}): {
  dateFromInclusive: string | null;
  dateToInclusive: string | null;
} => ({
  dateFromInclusive:
    df && df.dateFromInclusive ? ldToDb(df.dateFromInclusive) : null,
  dateToInclusive: df && df.dateToInclusive ? ldToDb(df.dateToInclusive) : null,
});

const dateFiltersFromDb = (df?: any) => ({
  dateFromInclusive:
    df && df.dateFromInclusive ? ldFromDb(df.dateFromInclusive) : undefined,
  dateToInclusive:
    df && df.dateToInclusive ? ldFromDb(df.dateToInclusive) : undefined,
});

const parentalLeaveToDb = (p: ParentalLeavePolicy) => ({
  startsFrom: ldToDb(p.startsFrom),
  afterPolicy: p.afterPolicy,
  sequentialRules: p.sequentialRules.map((r) =>
    r.amountType === "smp"
      ? {
          amountType: "smp",
          chronoUnits: chronoToDb(r.chronoUnits),
          amountOfChronoUnits: r.amountOfChronoUnits,
        }
      : {
          amountType: "percentage",
          percentage: r.percentage,
          chronoUnits: chronoToDb(r.chronoUnits),
          amountOfChronoUnits: r.amountOfChronoUnits,
        },
  ),
});

const parentalLeaveFromDb = (p: any): ParentalLeavePolicy => ({
  startsFrom: ldFromDb(p.startsFrom),
  afterPolicy: p.afterPolicy ?? "full-pay",
  sequentialRules: p.sequentialRules.map((r: any) =>
    (r.amountType ?? "percentage") === "smp"
      ? {
          amountType: "smp",
          chronoUnits: chronoFromDb(r.chronoUnits),
          amountOfChronoUnits: r.amountOfChronoUnits,
        }
      : {
          amountType: "percentage",
          percentage: r.percentage ?? 1,
          chronoUnits: chronoFromDb(r.chronoUnits),
          amountOfChronoUnits: r.amountOfChronoUnits,
        },
  ),
});

export const cashflowItemToDb = (item: CashflowItem) => {
  switch (item.type) {
    case "INCOME_ITEM":
      return {
        ...item,
        recurrence: recurrenceToDb(item.recurrence),
        dateFilters: dateFiltersToDb(item.dateFilters),
        parentalLeavePolicy: item.parentalLeavePolicy
          ? parentalLeaveToDb(item.parentalLeavePolicy)
          : null,
        redundancy: item.redundancy
          ? {
              endDate: ldToDb(item.redundancy.endDate),
              paidOn: item.redundancy.paidOn
                ? ldToDb(item.redundancy.paidOn)
                : null,
              weeks: item.redundancy.weeks,
              payBasis: item.redundancy.payBasis,
              noticeMonths: item.redundancy.noticeMonths ?? null,
              holidayDays: item.redundancy.holidayDays ?? null,
            }
          : null,
        bonuses: (item.bonuses || []).map((b) => ({
          date: ldToDb(b.date),
          kind: b.kind ?? "percentage",
          percentage: b.percentage ?? null,
          amountPence: b.amountPence ?? null,
        })),
        taxable: item.taxable || false,
        userId: item.userId || null,
      };

    case "OUTGOINGS_ITEM":
      return {
        ...item,
        recurrence: recurrenceToDb(item.recurrence),
        dateFilters: dateFiltersToDb(item.dateFilters),
      };
  }
};

export const cashflowItemFromDb = (raw: unknown): CashflowItem => {
  const data = dbCashflowItemSchema.parse(raw);

  switch (data.type) {
    case "INCOME_ITEM":
      return {
        type: data.type,
        id: data.id,
        taxable: data.taxable,
        userId: data.userId || "",
        description: data.description,
        amountPence: data.amountPence,
        recurrence: recurrenceFromDb(data.recurrence),
        dateFilters: dateFiltersFromDb(data.dateFilters),
        parentalLeavePolicy: data.parentalLeavePolicy
          ? parentalLeaveFromDb(data.parentalLeavePolicy)
          : undefined,
        redundancy: data.redundancy
          ? {
              endDate: ldFromDb(data.redundancy.endDate),
              paidOn: data.redundancy.paidOn
                ? ldFromDb(data.redundancy.paidOn)
                : undefined,
              weeks: data.redundancy.weeks,
              payBasis: data.redundancy.payBasis ?? "full",
              noticeMonths: data.redundancy.noticeMonths ?? undefined,
              holidayDays: data.redundancy.holidayDays ?? undefined,
            }
          : undefined,
        bonuses: data.bonuses?.map((b) => ({
          date: ldFromDb(b.date),
          kind: b.kind ?? "percentage",
          percentage: b.percentage ?? undefined,
          amountPence: b.amountPence ?? undefined,
        })),
      };

    case "OUTGOINGS_ITEM":
      return {
        ...data,
        recurrence: recurrenceFromDb(data.recurrence),
        dateFilters: dateFiltersFromDb(data.dateFilters),
      };
  }
};
